import {
  DataObjectGroupProcessingStateType,
  isSafeFiniteInteger,
  useInputDataGroupProcessingStateRefreshQuery,
  useInterval,
  useRemoveInputDataAggregateRefreshQueries,
  useSystemClassifierItem,
} from '@atlas-frontend/sdk';
import { useCallback, useMemo } from 'react';

type UseRefreshInputDataGroupProcessingStatesOptions = {
  isAutoRefreshPaused?: () => boolean;
};

export function useRefreshInputDataGroupProcessingStates(
  content?: DataObjectGroupProcessingStateType[],
  dataManager?: string,
  options?: UseRefreshInputDataGroupProcessingStatesOptions,
) {
  const { isAutoRefreshPaused } = options ?? {};
  const manual = useSystemClassifierItem('SYSTEM_CONFIGURATION', 'AUTOREFRESH');

  const interval = useMemo(() => {
    if (!isSafeFiniteInteger(manual?.parameters)) {
      return null;
    }

    return Number(manual?.parameters) * 1000;
  }, [manual?.parameters]);
  const ids = useMemo(() => content?.map((value) => value.id) ?? [], [content]);

  const { data: updates, refetch } =
    useInputDataGroupProcessingStateRefreshQuery(ids, dataManager, {
      enabled: !!(ids.length && interval),
      initialData: [],
      refetchInterval: false,
    });

  useInterval(
    () => {
      if (!isAutoRefreshPaused?.()) {
        void refetch();
      }
    },
    ids.length ? (interval ?? 0) : 0,
  );

  const removeQueryData = useRemoveInputDataAggregateRefreshQueries();

  return {
    data: useMemo(() => {
      if (!content?.length || !updates?.length) {
        return content;
      }

      const updateMap = new Map(updates.map((update) => [update.id, update]));

      return content.map((processingState) => {
        const update = updateMap.get(processingState.id);
        if (!update) {
          return processingState;
        }

        return {
          ...processingState,
          ...update,
        };
      });
    }, [content, updates]),
    refresh: useCallback(() => {
      removeQueryData();
      void refetch();
    }, [removeQueryData, refetch]),
  };
}
